/**
 * Workspace slice for managing organizations, workspaces, and members
 */

import type { StateCreator } from 'zustand';
import type { WorkspaceSlice, AppStore, Workspace, Organization, WorkspaceMember } from '../types';
import { api } from '@/lib/api';

export const createWorkspaceSlice: StateCreator<AppStore, [], [], WorkspaceSlice> = (set, get) => ({
  // Initial state
  currentWorkspace: null,
  currentOrganization: null,
  workspaces: [],
  members: [],
  isLoadingWorkspaces: false,
  isLoadingMembers: false,
  isSwitchingWorkspace: false,
  workspaceError: null,

  // Actions
  setCurrentWorkspace: (workspace) => {
    set({ currentWorkspace: workspace });

    // Store the current workspace ID in sessionStorage for persistence across page refreshes
    if (workspace) {
      sessionStorage.setItem('currentWorkspaceId', workspace.id);
    } else {
      sessionStorage.removeItem('currentWorkspaceId');
    }
  },

  setCurrentOrganization: (organization) => set({ currentOrganization: organization }),

  setWorkspaces: (workspaces) => set({ workspaces }),

  setMembers: (members) => set({ members }),

  addWorkspace: (workspace) =>
    set((state) => ({
      workspaces: [...state.workspaces, workspace],
    })),

  updateWorkspace: (id, updates) =>
    set((state) => ({
      workspaces: state.workspaces.map((ws) => (ws.id === id ? { ...ws, ...updates } : ws)),
      currentWorkspace:
        state.currentWorkspace?.id === id
          ? { ...state.currentWorkspace, ...updates }
          : state.currentWorkspace,
    })),

  removeWorkspace: (id) =>
    set((state) => ({
      workspaces: state.workspaces.filter((ws) => ws.id !== id),
      currentWorkspace: state.currentWorkspace?.id === id ? null : state.currentWorkspace,
    })),

  switchWorkspace: async (workspaceId) => {
    const { addNotification } = get();

    set({ isSwitchingWorkspace: true, workspaceError: null });

    try {
      // Find the workspace in the list
      const workspace = get().workspaces.find((ws) => ws.id === workspaceId);

      if (!workspace) {
        throw new Error('Workspace not found');
      }

      // Update current workspace
      set({ currentWorkspace: workspace });

      // Note: Members are included in workspace details endpoint, not fetched separately
      // await get().fetchMembers(workspaceId)

      // Notify success
      addNotification({
        type: 'success',
        title: 'Workspace switched',
        message: `Now working in ${workspace.name}`,
        duration: 3000,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to switch workspace';
      set({ workspaceError: errorMessage });

      addNotification({
        type: 'error',
        title: 'Failed to switch workspace',
        message: errorMessage,
        duration: 5000,
      });

      throw error;
    } finally {
      set({ isSwitchingWorkspace: false });
    }
  },

  fetchWorkspaces: async () => {
    // Prevent multiple concurrent calls
    const currentState = get();
    if (currentState.isLoadingWorkspaces) {
      return; // Already loading, skip this call
    }

    set({ isLoadingWorkspaces: true, workspaceError: null });

    try {
      // Fetch organization and workspaces in PARALLEL for faster loading.
      // Use allSettled so we can inspect each result — a 404 from the org
      // endpoint specifically means the user hasn't created an org yet
      // (needs onboarding), which is handled by the auth flow, not an error toast.
      const [orgResult, workspacesResult] = await Promise.allSettled([
        api.get<Organization>('organizations/current'),
        api.get<{ workspaces: Workspace[] }>('workspaces'),
      ]);

      // If the org call returned 404, the user needs onboarding — exit silently.
      // The useAuthCheck hook will redirect them to /onboarding.
      if (orgResult.status === 'rejected') {
        const orgStatus = (orgResult.reason as any)?.status;
        if (orgStatus === 404) {
          // Expected for new users — no notification needed
          return;
        }
        // Unexpected org error — surface it
        throw orgResult.reason;
      }

      const orgResponse = orgResult.value;
      set({ currentOrganization: orgResponse });

      // Handle workspaces result
      if (workspacesResult.status === 'rejected') {
        throw workspacesResult.reason;
      }

      const workspacesList = workspacesResult.value?.workspaces || [];
      set({ workspaces: workspacesList });

      // Set the current workspace if not already set
      const currentWorkspaceId = sessionStorage.getItem('currentWorkspaceId');
      const currentWorkspace = get().currentWorkspace;

      if (!currentWorkspace && workspacesList.length > 0) {
        // Try to restore from sessionStorage or use the default/first workspace
        const workspaceToSet = currentWorkspaceId
          ? workspacesList.find((ws) => ws.id === currentWorkspaceId)
          : workspacesList.find((ws) => ws.isDefault) || workspacesList[0];

        if (workspaceToSet) {
          set({ currentWorkspace: workspaceToSet });
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch workspaces';
      set({ workspaceError: errorMessage });

      get().addNotification({
        type: 'error',
        title: 'Failed to load workspaces',
        message: errorMessage,
        duration: 5000,
      });

      throw error;
    } finally {
      set({ isLoadingWorkspaces: false });
    }
  },

  fetchMembers: async (workspaceId) => {
    set({ isLoadingMembers: true });

    try {
      // FIXED: removed leading slash
      const members = await api.get<WorkspaceMember[]>(`workspaces/${workspaceId}/members`);
      set({ members });
    } catch (error) {
      console.error('Failed to fetch workspace members:', error);
      // Don't throw here as members are not critical for workspace functionality
      set({ members: [] });
    } finally {
      set({ isLoadingMembers: false });
    }
  },

  setWorkspaceError: (error) => set({ workspaceError: error }),

  clearWorkspaceData: () =>
    set({
      currentWorkspace: null,
      currentOrganization: null,
      workspaces: [],
      members: [],
      isLoadingWorkspaces: false,
      isLoadingMembers: false,
      isSwitchingWorkspace: false,
      workspaceError: null,
    }),
});
